import { useState, useRef, useCallback } from 'react';
import { Settings, Zap, Upload, Plus } from 'lucide-react';
import { VideoFile, VideoSettings } from '@/types';
import VideoUploadArea from './VideoUploadArea';
import VideoSettingsPanel from './VideoSettingsPanel';
import VideoListCompact from './VideoListCompact';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

interface VideoConverterPageProps {
  videos: VideoFile[];
  settings: VideoSettings;
  isProcessing: boolean;
  onFileSelect: (files: FileList) => void;
  onProcessVideo: (video: VideoFile) => void;
  onProcessAll: () => void;
  onDownloadVideo: (video: VideoFile) => void;
  onDownloadAll: () => void;
  onDownloadAllAsZip: () => void;
  onRemoveVideo: (id: string) => void;
  onClearAll: () => void;
  onUpdateSettings: (key: keyof VideoSettings, value: any) => void;
  isDarkMode: boolean;
}

export default function VideoConverterPage({
  videos,
  settings,
  isProcessing,
  onFileSelect,
  onProcessVideo,
  onProcessAll,
  onDownloadVideo,
  onDownloadAll,
  onDownloadAllAsZip,
  onRemoveVideo,
  onClearAll,
  onUpdateSettings,
  isDarkMode
}: VideoConverterPageProps) {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasVideos = videos.length > 0;

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      onFileSelect(e.target.files);
      // Limpar o input para permitir seleção do mesmo arquivo novamente
      e.target.value = '';
    }
  };

  // Handlers para drag & drop na área Empty State
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      onFileSelect(files);
    }
  }, [onFileSelect]);

  const handleEmptyStateClick = () => {
    fileInputRef.current?.click();
  };

  const handleProcessAll = () => {
    console.log('🚀 BOTÃO CONVERTER TODOS CLICADO! (VideoConverterPage)');
    console.log('📊 Estado atual:', {
      totalVideos: videos.length,
      pendingVideos: videos.filter(vid => vid.status === 'pending').length,
      isProcessing,
      videos: videos.map(vid => ({
        id: vid.id,
        name: vid.file?.name,
        status: vid.status,
        hasFile: !!vid.file
      }))
    });
    onProcessAll();
  };

  return (
    <div className="w-full max-w-none space-y-3 sm:space-y-4">
      {/* Botão de Configurações */}
      <div className="flex justify-end">
        <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
          <DialogTrigger asChild>
            <Button 
              variant="outline" 
              size="sm"
              className="flex items-center space-x-1.5 text-xs sm:text-sm"
            >
              <Settings className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Configurações</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" style={{ backgroundColor: '#383D52' }}>
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-2">
                <Settings className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Configurações de Conversão</span>
              </DialogTitle>
              <DialogDescription>
                Configure os parâmetros padrão para conversão de vídeos
              </DialogDescription>
            </DialogHeader>
            <VideoSettingsPanel 
              settings={settings}
              onUpdateSettings={onUpdateSettings}
              onClose={() => setIsSettingsOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Área de Upload - Mostrar apenas quando não há vídeos */}
      {!hasVideos && (
        <div className="w-full">
          <VideoUploadArea onFileSelect={onFileSelect} />
        </div>
      )}

      {/* Input file oculto para adicionar mais arquivos */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="video/*,.mp4,.webm,.ogv,.m4v,.mov,.avi,.mkv,.flv,.wmv,.3gp,.divx,.xvid,.mpg,.mpeg,.m2v,.ts,.mts,.m2ts,.vob,.f4v,.asf,.rm,.rmvb"
        onChange={handleFileInputChange}
        className="hidden"
      />

      {/* Quick Actions */}
      {hasVideos && (
        <div className={`flex flex-wrap items-center justify-between gap-2 p-3 rounded-lg border ${
          isDarkMode 
            ? 'bg-[#2A2E3F] border-[#4A4F64]' 
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-md ${
              isDarkMode ? 'bg-green-500/20' : 'bg-green-50'
            }`}>
              <Zap className="w-4 h-4 text-green-400" />
            </div>
            <div>
              <p className={`text-sm text-white`}>
                {videos.length} {videos.length === 1 ? 'vídeo' : 'vídeos'}
              </p>
              <p className={`text-sm text-[#94A3B8]`}>
                Pronto para conversão
              </p>
            </div>
          </div>

          <Button
            onClick={() => fileInputRef.current?.click()}
            variant="outline"
            size="sm"
            className="flex items-center space-x-1.5 text-xs sm:text-sm h-8"
          >
            <Plus className="w-4 h-4" />
            <span>{videos.length === 1 ? 'Escolher vídeo' : 'Escolher vídeos'}</span>
          </Button>
        </div>
      )}

      {/* Lista de Vídeos */}
      {hasVideos && (
        <div className="w-full">
          <VideoListCompact 
            videos={videos}
            onProcessVideo={onProcessVideo}
            onProcessAll={handleProcessAll}
            onDownloadVideo={onDownloadVideo}
            onDownloadAll={onDownloadAll}
            onDownloadAllAsZip={onDownloadAllAsZip}
            onRemoveVideo={onRemoveVideo}
            onClearAll={onClearAll}
            isProcessing={isProcessing}
            isDarkMode={isDarkMode}
            settings={settings}
          />
        </div>
      )}

      {/* Empty State com Drag & Drop */}
      {!hasVideos && (
        <div 
          className={`text-center py-8 px-4 rounded-lg border-2 border-dashed cursor-pointer transition-all duration-300 ${
            isDragActive
              ? isDarkMode 
                ? 'border-red-400 bg-red-500/10' 
                : 'border-red-500 bg-red-50'
              : isDarkMode 
                ? 'border-[#4A4F64] bg-[#2A2E3F]/50 hover:border-red-400/50 hover:bg-red-500/5' 
                : 'border-gray-300 bg-gray-50 hover:border-red-400 hover:bg-red-50'
          }`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={handleEmptyStateClick}
        >
          <Upload className={`w-10 h-10 mx-auto mb-3 transition-colors duration-300 ${
            isDragActive
              ? isDarkMode ? 'text-red-400' : 'text-red-500'
              : isDarkMode ? 'text-gray-500 hover:text-red-400' : 'text-gray-400 hover:text-red-500'
          }`} />
          <h3 className={`text-lg font-medium mb-2 transition-colors duration-300 ${
            isDragActive
              ? isDarkMode ? 'text-red-300' : 'text-red-600'
              : isDarkMode ? 'text-white hover:text-red-300' : 'text-gray-900 hover:text-red-600'
          }`}>
            {isDragActive ? 'Solte seus vídeos aqui' : 'Nenhum vídeo carregado'}
          </h3>
          <p className={`text-sm transition-colors duration-300 ${
            isDragActive
              ? isDarkMode ? 'text-red-400' : 'text-red-500'
              : isDarkMode ? 'text-gray-400 hover:text-red-400' : 'text-gray-600 hover:text-red-500'
          }`}>
            {isDragActive ? 'Solte para adicionar à lista' : 'Arraste e solte seus vídeos ou clique para selecionar'}
          </p>
        </div>
      )}
    </div>
  );
} 